import { fieldVariants } from '@/shared/components/ui/field/field.tokens';
import { cn } from '@/shared/utils/cn/cn.util';

import { type ScalarFieldIslandProps } from './ScalarFieldIsland.types';

/**
 * One field box holding more than one control — a number beside its unit, a
 * number beside its `%`.
 *
 * The whole point is that it reads as **one** field: one border, one focus ring,
 * one height, taken from `fieldVariants` so it is exactly the field beside it.
 * Its children wear `FIELD_SLOT_CLASSES` and give their own border and ring
 * back, because two rings around one control is worse than none.
 *
 * `isInvalid` rather than `aria-invalid`: ARIA gives a `<div>` no invalid state
 * (it holds no value), so the border needs the variant the field surface keeps
 * for exactly this case. The real `aria-invalid` goes on the input inside.
 *
 * Presentation only (U5).
 */
export function ScalarFieldIsland({
  className,
  size,
  isInvalid = false,
  children,
  adornment,
}: ScalarFieldIslandProps) {
  return (
    <div
      // The hook the measured walk finds every field box by. A class would do it
      // today and stop doing it the moment the token changes; this is a contract.
      data-scalar-island
      // Not a `role="group"`: the island is a box, and the control that owns it
      // has already decided whether it is a group and what names it.
      className={cn(fieldVariants({ fieldSize: size, shape: 'island', isInvalid }), className)}
    >
      {children}
      {adornment}
    </div>
  );
}
